import { useState, useEffect } from 'react';

// Request monitoring utility for tracking API requests
interface RequestLog {
  timestamp: number;
  url: string;
  method: string;
  duration: number;
  status: number;
  size?: number;
  cached: boolean;
  _source: string; // component or context that made the request
}

interface RequestMetrics {
  totalRequests: number;
  cacheHits: number;
  averageDuration: number;
  requestsByUrl: Map<string, RequestLog[]>;
  requestsBySource: Map<string, RequestLog[]>;
}

class RequestMonitor {
  private logs: RequestLog[] = [];
  private listeners: ((metrics: RequestMetrics) => void)[] = [];
  private activeRequests: Map<string, number> = new Map();

  // Log a request
  logRequest(request: Omit<RequestLog, 'timestamp'>) {
    const log: RequestLog = {
      ...request,
      timestamp: Date.now()
    };

    this.logs.push(log);
    this.notifyListeners();
    
    // Keep only last 1000 logs to prevent memory leaks
    if (this.logs.length > 1000) {
      this.logs = this.logs.slice(-1000);
    }
  }

  // Start timing a request
  startRequest(url: string, _source: string = 'unknown'): string {
    const requestId = `${url}-${Date.now()}-${Math.random()}`;
    this.activeRequests.set(requestId, Date.now());
    return requestId;
  }

  // End timing a request
  endRequest(
    requestId: string,
    url: string,
    method: string,
    status: number,
    cached: boolean = false,
    source: string = 'unknown',
    size?: number
  ) {
    const startTime = this.activeRequests.get(requestId);
    if (!startTime) return;

    const duration = Date.now() - startTime;
    this.activeRequests.delete(requestId);

    this.logRequest({
      url,
      method,
      duration,
      status,
      size,
      cached,
      _source: source
    });
  }

  // Get current metrics
  getMetrics(): RequestMetrics {
    const recentLogs = this.logs.slice(-100); // Last 100 requests
    const totalRequests = recentLogs.length;
    const cacheHits = recentLogs.filter(log => log.cached).length;
    const averageDuration = totalRequests > 0 
      ? recentLogs.reduce((sum, log) => sum + log.duration, 0) / totalRequests 
      : 0;

    const requestsByUrl = new Map<string, RequestLog[]>();
    const requestsBySource = new Map<string, RequestLog[]>();

    recentLogs.forEach(log => {
      // Group by URL
      const urlLogs = requestsByUrl.get(log.url) || [];
      urlLogs.push(log);
      requestsByUrl.set(log.url, urlLogs);

      // Group by source
      const sourceLogs = requestsBySource.get(log._source) || [];
      sourceLogs.push(log);
      requestsBySource.set(log._source, sourceLogs);
    });

    return {
      totalRequests,
      cacheHits,
      averageDuration,
      requestsByUrl,
      requestsBySource
    };
  }

  // Get requests for a specific page/component
  getRequestsBySource(_source: string): RequestLog[] {
    return this.logs.filter(log => log._source === _source);
  }

  // Get requests in the last N milliseconds
  getRecentRequests(milliseconds: number = 5000): RequestLog[] {
    const cutoff = Date.now() - milliseconds;
    return this.logs.filter(log => log.timestamp >= cutoff);
  }

  // Subscribe to metrics updates
  subscribe(listener: (metrics: RequestMetrics) => void) {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners() {
    const metrics = this.getMetrics();
    // Defer listener calls to prevent infinite loops during forEach iteration
    setTimeout(() => {
      this.listeners.forEach(listener => listener(metrics));
    }, 0);
  }

  // Clear all logs
  clearLogs() {
    this.logs = [];
    this.activeRequests.clear();
    this.notifyListeners();
  }

  // Export logs for analysis
  exportLogs(): RequestLog[] {
    return [...this.logs];
  }

  // Get performance summary
  getPerformanceSummary() {
    const metrics = this.getMetrics();
    const cacheHitRate = metrics.totalRequests > 0 ? (metrics.cacheHits / metrics.totalRequests) * 100 : 0;
    
    // Find slowest endpoints
    const endpointPerformance = Array.from(metrics.requestsByUrl.entries())
      .map(([url, logs]) => ({
        url,
        count: logs.length,
        averageDuration: logs.reduce((sum, log) => sum + log.duration, 0) / logs.length,
        totalDuration: logs.reduce((sum, log) => sum + log.duration, 0)
      }))
      .sort((a, b) => b.totalDuration - a.totalDuration);

    return {
      totalRequests: metrics.totalRequests,
      cacheHitRate: `${cacheHitRate.toFixed(1)}%`,
      averageDuration: `${metrics.averageDuration.toFixed(0)}ms`,
      slowestEndpoints: endpointPerformance.slice(0, 5),
      requestsBySource: Array.from(metrics.requestsBySource.entries())
        .map(([_source, logs]) => ({
          source: _source,
          count: logs.length,
          averageDuration: logs.reduce((sum, log) => sum + log.duration, 0) / logs.length
        }))
        .sort((a, b) => b.count - a.count)
    };
  }
}

// Singleton instance
export const requestMonitor = new RequestMonitor();

// React hook for using request monitor
export const useRequestMonitor = () => {
  const [metrics, setMetrics] = useState(() => requestMonitor.getMetrics());

  useEffect(() => {
    const unsubscribe = requestMonitor.subscribe(setMetrics);
    return unsubscribe;
  }, []);

  return {
    metrics,
    startRequest: requestMonitor.startRequest.bind(requestMonitor),
    endRequest: requestMonitor.endRequest.bind(requestMonitor),
    getPerformanceSummary: requestMonitor.getPerformanceSummary.bind(requestMonitor),
    clearLogs: requestMonitor.clearLogs.bind(requestMonitor)
  };
};
